import { useEffect, useRef, useState } from 'react';

import { TriangleAlert } from 'lucide-react';
import { useQueryClient } from 'react-query';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useUpdateFact from '@/api/mutations/useUpdateFact';
import { QueryEntity } from '@/api/types';
import Chip from '@/components/atoms/chip';
import TooltipIcon from '@/components/atoms/TooltipIcon';
import CommentPopup from '@/components/molecules/Comments';
import DateInputPopup from '@/components/molecules/DateInputPopup';
import EditableField from '@/components/molecules/EditableField';
import DropdownInput from '@/components/organisms/FactDropdown/DropdownInput';
import FactDocumentDetails from '@/components/organisms/FactDropdown/FactDocumentDetails';
import { RelevanceOptions } from '@/constants/filterOptions';
import { useOpenInconsistencyViewer } from '@/hooks/useOpenInconsistencyViewer';
import { Fact, Relevance } from '@/types';

const DocViewerFullFact = ({
  fact,
  showDocumentDetails = false,
  docId,
  showComments = true,
  gotoDocReference = () => {},
}: {
  fact: Fact;
  docId?: string;
  showDocumentDetails?: boolean;
  showComments?: boolean;
  gotoDocReference?: (docId: string, factId: string, pageNumber: number) => void;
}) => {
  const [dateText, setDateText] = useState(fact.factDateText);
  const [relevanceText, setRelevanceText] = useState<Relevance>(fact.relevance);

  // TODO: revisit this duplication, currently we have 4 to manage the loading states for each field
  const { mutateAsync: updateFactDescription, isLoading: isUpdatingDescription } = useUpdateFact(fact.caseId);
  const { mutateAsync: updateFactRelevance, isLoading: isUpdatingRelevance } = useUpdateFact(fact.caseId);
  const { mutateAsync: updateFactSignificance, isLoading: isUpdatingSignificance } = useUpdateFact(fact.caseId);
  const { mutateAsync: updateFactDateText, isLoading: isUpdatingDateText } = useUpdateFact(fact.caseId);

  const { openInconsistencyViewer } = useOpenInconsistencyViewer();
  const queryClient = useQueryClient();

  const handleUpdateFactDescription = async (value: string) => {
    await updateFactDescription({
      field: 'description',
      value,
      factId: fact.id,
    });
    if (docId) {
      queryClient.invalidateQueries([QueryEntity.CaseFacts, { docId }]);
    }
    trackCustomEvent(TrackingEvents.UpdateFactContent);
  };

  const handleUpdateFactSignificance = async (value: string) => {
    await updateFactSignificance({
      field: 'significance',
      value,
      factId: fact.id,
    });
    if (docId) {
      queryClient.invalidateQueries([QueryEntity.CaseFacts, { docId }]);
    }
    trackCustomEvent(TrackingEvents.UpdateFactContent);
  };

  const handleUpdateFactRelevance = async (value: Relevance) => {
    setRelevanceText(value);
    await updateFactRelevance({
      field: 'relevance',
      value,
      factId: fact.id,
    });
    if (docId) {
      queryClient.invalidateQueries([QueryEntity.CaseFacts, { docId }]);
    }
    trackCustomEvent(TrackingEvents.UpdateFactRelevance);
  };

  const handleUpdateFactDateText = async (value: string) => {
    setDateText(value);
    await updateFactDateText({
      field: 'factDateText',
      value,
      factId: fact.id,
    });
    if (docId) {
      queryClient.invalidateQueries([QueryEntity.CaseFacts, { docId }]);
    }
    trackCustomEvent(TrackingEvents.UpdateFactDate);
  };

  // For applying the fade effect
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  // Check if scrolled to bottom on mount
  useEffect(() => {
    const target = scrollContainerRef.current;
    if (target) {
      setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
    }
  }, [scrollContainerRef.current]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
  };

  return (
    <div
      className="max-h-96 overflow-auto mask-b-from-80% mask-b-to-99% text-xs transition-[mask-size] duration-200"
      style={{ maskSize: isScrolledToBottom ? '100% 120%' : '100% 100%' }}
      onScroll={handleScroll}
      ref={scrollContainerRef}
    >
      <div className="flex w-full items-center justify-between font-normal">
        <div className={`w-full pt-1.5`}>
          <div className="flex w-full justify-between">
            <div className={`flex items-center gap-1.75 pl-2 font-semibold`}>
              {fact.dateIsAmbiguous && (
                <TooltipIcon
                  triggerClassName="-mt-0.25"
                  tooltipContent={fact.dateIsAmbiguousText || 'Date format is ambiguous, please check'}
                  warning
                />
              )}

              {dateText ? dateText : '(Date Unknown)'}

              <DateInputPopup
                dateText={dateText}
                onUpdateDate={handleUpdateFactDateText}
                isLoading={isUpdatingDateText}
              />
            </div>
            {fact.inconsistencies && fact.inconsistencies.length ? (
              <Chip
                className="-mt-1 mr-6 mb-1 cursor-pointer bg-red-50 text-red-500 duration-150 hover:bg-red-100"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.OpenInconsistencyViewer);
                  fact.inconsistencies &&
                    openInconsistencyViewer(fact.inconsistencies.map((inconsistency) => inconsistency.inconsistencyId));
                }}
              >
                <TriangleAlert size={12} />
                <span>Disputed</span>
              </Chip>
            ) : (
              <></>
            )}
          </div>

          <div className="flex justify-between gap-1">
            <div className="relative max-h-84 w-full overflow-y-auto pt-1 pl-2 text-xs text-gray-900">
              <EditableField
                value={fact.description}
                onSave={handleUpdateFactDescription}
                isLoading={isUpdatingDescription}
              />
              {/* Gradient fade overlay that stays at the bottom of the viewport */}
              <div className="pointer-events-none sticky inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white to-transparent"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-x-0 border-t-0 border-dashed border-blue-200"></div>

      <div className="flex flex-col gap-2 py-3 pr-4">
        <div className="w-full">
          <div className="mb-2 flex items-center gap-1 px-2 font-semibold text-gray-800">
            Significance
            <TooltipIcon tooltipContent="A short analysis of the significance of the fact as it pertains to the key case issues and the context of the document it is extracted from." />
          </div>
          <div className="px-2 text-gray-800">
            <EditableField
              value={fact.significance}
              onSave={handleUpdateFactSignificance}
              isLoading={isUpdatingSignificance}
            />
          </div>
        </div>

        <div className="my-2 border border-x-0 border-t-0 border-dashed border-blue-200"></div>

        <div className="flex justify-between px-2">
          <div className="w-1/2">
            <div className="mb-1 flex items-center gap-1 font-semibold text-gray-800">
              Relevant
              <TooltipIcon tooltipContent="Is this fact relevant to the initial case synopsis." />
            </div>
            <div
              className={`${
                {
                  [Relevance.YES]: 'text-green-700',
                  [Relevance.NO]: 'text-red-500',
                  [Relevance.MAYBE]: 'text-orange-400',
                }[relevanceText] || ''
              }`}
            >
              <DropdownInput
                value={relevanceText}
                isLoading={isUpdatingRelevance}
                options={RelevanceOptions}
                onChange={handleUpdateFactRelevance}
              />
            </div>
          </div>

          <div className="w-1/2">
            <div className="mb-1 flex items-center gap-1 font-semibold text-gray-800">
              Classification
              <TooltipIcon tooltipContent="Fact classification. One of Primary, Secondary, Future or Litigation." />
            </div>
            <div>
              {fact.classification ? fact.classification.charAt(0).toUpperCase() + fact.classification.slice(1) : ''}
            </div>
          </div>
        </div>

        {showDocumentDetails && (
          <div>
            <div className="my-2 mb-3 border border-x-0 border-t-0 border-dashed border-blue-200"></div>

            <FactDocumentDetails fact={fact} gotoDocReference={gotoDocReference} fullDocRow={false} />
          </div>
        )}
      </div>

      {showComments && (
        <>
          <div className="mt-2 border-2 border-x-0 border-t-0 border-blue-200"></div>
          <div className="flex justify-between gap-10 rounded-b-lg bg-slate-100 px-4">
            <div>
              <div className="pt-2 pb-1 font-semibold">Comments</div>
              <div></div>
            </div>
            <div className="flex items-center gap-2 py-3">
              <CommentPopup
                caseId={fact.caseId}
                factId={fact.id}
                commentsCount={fact.comments.length}
                comments={fact.comments}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocViewerFullFact;
